import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, Layers, Pencil, Plus, RotateCcw, Tag } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { variantDisplayName } from '@aeris/catalog'
import type { ProductColourSummary, ProductSummary } from '@aeris/contracts'
import { formatQuantity, parseQuantity, ZERO_QUANTITY } from '@aeris/inventory'
import {
  Badge,
  Button,
  CheckboxField,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
} from '@aeris/ui'

import {
  ApiError,
  createProductColour,
  createProductColours,
  fetchProductColours,
  planProductColours,
  setProductColourStatus,
  updateProductColour,
} from '../lib/api.ts'
import {
  formatUnitPrice,
  parseTypedPrice,
  toEditableAmount,
  toWireAmount,
} from '../lib/price-format.ts'
import { useDebounced } from '../lib/use-debounced.ts'

import {
  LabelPrintDialog,
  type LabelPrintProduct,
} from './label-print-dialog.tsx'

/**
 * As cores do produto, na ficha (F176).
 *
 * *"O produto vai ser constituído pelas variações"*: cada cor com seu código,
 * seu código de barras e seu estoque. O preço é o do produto até alguém dar um
 * à cor — e volta a ser o do produto com um clique. A primeira cor é o próprio
 * produto: o preço dela se muda na aba Geral, e ela sai quando o produto sai.
 *
 * O estoque de cada cor entra pela aba Estoque, que já lista uma linha por cor:
 * nenhuma quantidade se digita aqui (seção 12 — saldo é movimento).
 *
 * Sem `<form>`: esta aba vive DENTRO do formulário do produto, e o React leva o
 * `submit` de um formulário no portal do diálogo até o de fora — salvar a cor
 * salvaria o produto junto.
 */

const errorText = (cause: unknown, fallback: string): string =>
  cause instanceof ApiError ? cause.message : fallback

type Editing =
  | { readonly mode: 'new' }
  | { readonly mode: 'edit'; readonly colour: ProductColourSummary }

export const ProductColoursPanel = ({
  product,
  canWrite,
}: {
  product: ProductSummary
  canWrite: boolean
}): ReactNode => {
  const productId = product.id
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Editing | null>(null)
  /** O diálogo de várias cores de uma vez (F191). */
  const [batch, setBatch] = useState(false)
  /** A etiqueta de uma cor: o código de barras e o preço DELA (F176). */
  const [label, setLabel] = useState<LabelPrintProduct | null>(null)
  const labelOf = (colour: ProductColourSummary): LabelPrintProduct => ({
    variantId: colour.id,
    productId,
    name: variantDisplayName(product.name, colour.name),
    sku: colour.sku,
    gtin: colour.gtin,
    salePrice: colour.effectivePrice,
    lastPurchaseAt: null,
    brand: product.brand,
    unit: product.variants[0]?.unit ?? null,
    supplierReference: colour.supplierReference,
    location: product.location ?? null,
  })
  const [error, setError] = useState<string | null>(null)

  const colours = useQuery({
    queryKey: ['catalog', 'product', productId, 'variants'],
    queryFn: async ({ signal }) => await fetchProductColours(productId, signal),
  })

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['catalog', 'product', productId, 'variants'],
    })
    // A lista, a busca e o estoque mostram as cores também.
    await queryClient.invalidateQueries({ queryKey: ['products'] })
    await queryClient.invalidateQueries({ queryKey: ['stock-balances'] })
  }

  const status = useMutation({
    mutationFn: async (input: {
      colour: ProductColourSummary
      action: 'archive' | 'restore'
    }) =>
      await setProductColourStatus(
        productId,
        input.colour.id,
        input.action,
        input.colour.version
      ),
    onSuccess: async () => {
      setError(null)
      await refresh()
    },
    onError: (cause: unknown) => {
      setError(errorText(cause, 'Não foi possível mudar a cor.'))
    },
  })

  if (colours.isPending) return <Spinner label="Carregando as cores" />
  if (colours.isError) {
    return (
      <InlineAlert tone="danger">
        Não foi possível carregar as cores.
      </InlineAlert>
    )
  }

  const list = colours.data.colours
  const named = list.some((colour) => colour.name !== null)

  return (
    <div className="flex flex-col gap-3">
      <p className="text-(length:--text-sm) text-(--text-secondary)">
        Cada cor tem seu código, seu código de barras e seu estoque. O código
        sai do código do produto (-1, -2, -3…) e a barra é gerada a partir dele.
        Sem preço próprio, a cor segue o preço do produto.
      </p>

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      {!named ? (
        <EmptyState
          title="Este produto ainda não tem cores"
          description="Adicione a primeira cor nova: o sistema pergunta o nome da cor que o produto já tem (ex.: Preto)."
        />
      ) : (
        <TableContainer>
          <Table aria-label="Cores do produto">
            <TableHead>
              <TableRow>
                <TableHeaderCell>Cor</TableHeaderCell>
                <TableHeaderCell>Código</TableHeaderCell>
                <TableHeaderCell>Código de barras</TableHeaderCell>
                <TableHeaderCell numeric>Preço</TableHeaderCell>
                <TableHeaderCell numeric>Estoque</TableHeaderCell>
                <TableHeaderCell>
                  <span className="sr-only">Ações</span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((colour) => (
                <TableRow key={colour.id}>
                  <TableCell>
                    <span className="flex flex-wrap items-center gap-2">
                      {colour.name ?? '—'}
                      {colour.isDefault ? (
                        <Badge tone="neutral">principal</Badge>
                      ) : null}
                      {colour.status === 'archived' ? (
                        <Badge tone="warning">arquivada</Badge>
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell className="font-(--font-mono)">
                    {colour.sku}
                  </TableCell>
                  <TableCell className="font-(--font-mono)">
                    {colour.gtin ?? '—'}
                  </TableCell>
                  <TableCell numeric>
                    {colour.effectivePrice === null ? (
                      '—'
                    ) : colour.ownPrice === null && !colour.isDefault ? (
                      <span className="text-(--text-muted)">
                        {`segue o produto (${formatUnitPrice(colour.effectivePrice)})`}
                      </span>
                    ) : (
                      formatUnitPrice(colour.effectivePrice)
                    )}
                  </TableCell>
                  <TableCell numeric>
                    {formatQuantity(
                      parseQuantity(colour.onHand) ?? ZERO_QUANTITY
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="flex justify-end gap-1">
                      {colour.status === 'active' ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          aria-label={`Etiqueta da cor ${colour.name ?? ''}`}
                          onClick={() => {
                            setLabel(labelOf(colour))
                          }}
                        >
                          <Tag aria-hidden="true" className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {canWrite ? (
                        <>
                          {colour.status === 'active' ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              aria-label={`Editar a cor ${colour.name ?? ''}`}
                              onClick={() => {
                                setEditing({ mode: 'edit', colour })
                              }}
                            >
                              <Pencil aria-hidden="true" className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {colour.isDefault ? null : (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              disabled={status.isPending}
                              aria-label={
                                colour.status === 'active'
                                  ? `Arquivar a cor ${colour.name ?? ''}`
                                  : `Restaurar a cor ${colour.name ?? ''}`
                              }
                              onClick={() => {
                                status.mutate({
                                  colour,
                                  action:
                                    colour.status === 'active'
                                      ? 'archive'
                                      : 'restore',
                                })
                              }}
                            >
                              {colour.status === 'active' ? (
                                <Archive
                                  aria-hidden="true"
                                  className="h-4 w-4"
                                />
                              ) : (
                                <RotateCcw
                                  aria-hidden="true"
                                  className="h-4 w-4"
                                />
                              )}
                            </Button>
                          )}
                        </>
                      ) : null}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {canWrite ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setEditing({ mode: 'new' })
            }}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            Adicionar cor
          </Button>
          {/*
            O segundo botão, e não um só que faz as duas coisas (F191).

            "Adicionar cor" é onde se cria UMA cor com preço próprio, código
            digitado, mínimo e referência do fornecedor — uma conversa de cada
            vez, e continua sendo. "Adicionar várias" é o caso do dono: seis
            nomes de cor de um chassi, tudo automático. Juntar os dois num
            diálogo só faria o caminho comum carregar o formulário inteiro.
          */}
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setBatch(true)
            }}
          >
            <Layers aria-hidden="true" className="h-4 w-4" />
            Adicionar várias
          </Button>
        </div>
      ) : null}

      {label === null ? null : (
        <LabelPrintDialog
          product={label}
          onClose={() => {
            setLabel(null)
          }}
        />
      )}

      {editing === null ? null : (
        <ColourDialog
          productId={productId}
          editing={editing}
          needsDefaultName={!named}
          onClose={() => {
            setEditing(null)
          }}
          onSaved={async () => {
            setEditing(null)
            await refresh()
          }}
        />
      )}

      {!batch ? null : (
        <ColourBatchDialog
          productId={productId}
          needsDefaultName={!named}
          onClose={() => {
            setBatch(false)
          }}
          onSaved={async () => {
            setBatch(false)
            // UM refresh no fim, e não três por cor — era isso que fazia seis
            // cores custarem dezoito invalidações de cache.
            await refresh()
          }}
        />
      )}
    </div>
  )
}

/**
 * Várias cores de uma vez (F191).
 *
 * O dono, duas vezes: *"tivesse algum esquema de criar mais rápido as cor"*.
 * Seis cores de um chassi eram seis idas ao "Adicionar cor", com o diálogo
 * fechando a cada uma.
 *
 * ## Por que a prévia não é enfeite
 *
 * Código e código de barras nascem automáticos, e é justamente por isso que
 * têm de aparecer ANTES. É a doutrina da F181: a tela não inventa número que o
 * alocador não vai dar, então quem calcula é o servidor — a mesma conta que
 * vai gravar, e não uma parecida feita aqui.
 *
 * A linha que mais importa é a do topo: ao criar a primeira cor, o código do
 * PRODUTO deixa de ser `6455` e passa a `6455-1`. Quem não vê isso antes
 * descobre depois, com etiqueta impressa na gaveta.
 */
const ColourBatchDialog = ({
  productId,
  needsDefaultName,
  onClose,
  onSaved,
}: {
  productId: string
  needsDefaultName: boolean
  onClose: () => void
  onSaved: () => Promise<void>
}): ReactNode => {
  const [text, setText] = useState('')
  const [defaultName, setDefaultName] = useState('')
  const [error, setError] = useState<string | null>(null)

  /*
   * Uma cor por linha, e a vírgula também serve.
   *
   * Quem cola de uma planilha traz uma por linha; quem digita na hora escreve
   * "preto, azul, vermelho" sem pensar. Aceitar os dois custa uma linha e evita
   * a pergunta "por que só entrou uma cor?".
   */
  const names = text
    .split(/[\n,]/)
    .map((part) => part.trim())
    .filter((part) => part !== '')

  const debounced = useDebounced(
    `${names.join('\n')}\u0000${defaultName}`
  )

  const ready = names.length > 0 && (!needsDefaultName || defaultName.trim() !== '')

  const plan = useQuery({
    queryKey: ['catalog', 'product', productId, 'colour-batch', debounced],
    enabled: ready,
    queryFn: async ({ signal }) =>
      await planProductColours(
        productId,
        {
          names: debounced.split('\u0000')[0]?.split('\n') ?? [],
          ...(needsDefaultName ? { defaultName: defaultName.trim() } : {}),
        },
        signal
      ),
    // A prévia que falha não é erro da tela: o nome repetido é recusado aqui
    // pelo mesmo caminho que recusaria ao gravar, e a mensagem já explica.
    retry: false,
  })

  const save = useMutation({
    mutationFn: async () =>
      await createProductColours(productId, {
        names,
        ...(needsDefaultName ? { defaultName: defaultName.trim() } : {}),
      }),
    onSuccess: async () => {
      await onSaved()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não deu para criar as cores.'
      )
    },
  })

  const planError =
    plan.error instanceof ApiError ? plan.error.message : null

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent title="Adicionar várias cores">
        <div className="flex flex-col gap-3">
          {needsDefaultName ? (
            <Field
              label="Cor que o produto já tem"
              required
              hint="O cadastro de hoje vira esta cor, com o estoque e o preço que já tem."
            >
              {(field) => (
                <Input
                  {...field}
                  value={defaultName}
                  placeholder="ex.: Preto"
                  onChange={(event) => {
                    setDefaultName(event.target.value)
                  }}
                />
              )}
            </Field>
          ) : null}

          <Field
            label="Cores"
            required
            hint="Uma por linha. Vírgula também serve. O código e o código de barras saem automáticos."
          >
            {(field) => (
              <Textarea
                {...field}
                rows={6}
                value={text}
                placeholder={'Preto\nAzul\nVermelho'}
                onChange={(event) => {
                  setText(event.target.value)
                }}
              />
            )}
          </Field>

          {planError !== null ? (
            <InlineAlert tone="danger">{planError}</InlineAlert>
          ) : !ready ? null : plan.isPending ? (
            <div className="flex items-center gap-2 text-(length:--text-sm) text-(--text-secondary)">
              <Spinner className="h-4 w-4" />
              Calculando os códigos…
            </div>
          ) : plan.data === undefined ? null : (
            <div className="flex flex-col gap-2">
              {plan.data.renamesDefaultTo === null ? null : (
                <InlineAlert tone="info">
                  A cor que o produto já tem passa a ter o código{' '}
                  <strong>{plan.data.renamesDefaultTo}</strong>. O código de
                  barras dela não muda.
                </InlineAlert>
              )}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Cor</TableHeaderCell>
                      <TableHeaderCell>Código</TableHeaderCell>
                      <TableHeaderCell>Código de barras</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plan.data.lines.map((line) => (
                      <TableRow key={line.sku}>
                        <TableCell>{line.name}</TableCell>
                        <TableCell className="font-(--font-mono)">
                          {line.sku}
                        </TableCell>
                        <TableCell className="font-(--font-mono)">
                          {line.gtin}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}

          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={save.isPending}
            disabled={!ready || planError !== null}
            onClick={() => {
              setError(null)
              save.mutate()
            }}
          >
            {names.length <= 1
              ? 'Criar cor'
              : `Criar ${String(names.length)} cores`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const ColourDialog = ({
  productId,
  editing,
  needsDefaultName,
  onClose,
  onSaved,
}: {
  productId: string
  editing: Editing
  needsDefaultName: boolean
  onClose: () => void
  onSaved: () => Promise<void>
}): ReactNode => {
  const current = editing.mode === 'edit' ? editing.colour : null
  const isDefault = current?.isDefault === true
  const [name, setName] = useState(current?.name ?? '')
  const [defaultName, setDefaultName] = useState('')
  const [sku, setSku] = useState(current?.sku ?? '')
  const [gtin, setGtin] = useState(current?.gtin ?? '')
  const [ownPrice, setOwnPrice] = useState(
    current?.ownPrice !== null && current !== null
  )
  const [price, setPrice] = useState(
    toEditableAmount(current?.ownPrice ?? null)
  )
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      const typedPrice = ownPrice ? parseTypedPrice(price) : null
      if (ownPrice && typedPrice === null) {
        throw new ApiError(422, 'price', 'Use um preço como 19,90.')
      }
      const salePrice = typedPrice === null ? null : toWireAmount(typedPrice)

      if (current === null) {
        return await createProductColour(productId, {
          name,
          ...(needsDefaultName ? { defaultName } : {}),
          ...(sku.trim() === '' ? {} : { sku: sku.trim().toUpperCase() }),
          gtin: gtin.trim(),
          ...(salePrice === null ? {} : { salePrice }),
        })
      }
      return await updateProductColour(productId, current.id, {
        version: current.version,
        name,
        sku: sku.trim().toUpperCase(),
        gtin: gtin.trim(),
        // A primeira cor tem o preço do produto: não se mexe aqui.
        ...(isDefault ? {} : { salePrice }),
      })
    },
    onSuccess: async () => {
      setError(null)
      await onSaved()
    },
    onError: (cause: unknown) => {
      setError(errorText(cause, 'Não foi possível salvar a cor.'))
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={
          current === null ? 'Adicionar cor' : `Editar ${current.name ?? 'cor'}`
        }
      >
        <div className="flex flex-col gap-3">
          {needsDefaultName && current === null ? (
            <Field
              label="Cor que o produto já tem"
              required
              hint="O cadastro de hoje vira esta cor, com o estoque e o preço que já tem."
            >
              {(field) => (
                <Input
                  {...field}
                  value={defaultName}
                  placeholder="ex.: Preto"
                  onChange={(event) => {
                    setDefaultName(event.target.value)
                  }}
                />
              )}
            </Field>
          ) : null}
          <Field label={current === null ? 'Nova cor' : 'Cor'} required>
            {(field) => (
              <Input
                {...field}
                value={name}
                placeholder="ex.: Azul marinho"
                onChange={(event) => {
                  setName(event.target.value)
                }}
              />
            )}
          </Field>
          <Field
            label="Código"
            hint={
              current === null
                ? 'Vazio: o código do produto com -2, -3… (a primeira cor fica com o -1).'
                : undefined
            }
          >
            {(field) => (
              <Input
                {...field}
                value={sku}
                onChange={(event) => {
                  setSku(event.target.value)
                }}
              />
            )}
          </Field>
          <Field
            label="Código de barras"
            hint={
              current === null
                ? 'Vazio: gerado a partir do código desta cor.'
                : 'O que o leitor do caixa lê nesta cor.'
            }
          >
            {(field) => (
              <Input
                {...field}
                value={gtin}
                inputMode="numeric"
                onChange={(event) => {
                  setGtin(event.target.value)
                }}
              />
            )}
          </Field>
          {isDefault ? (
            <p className="text-(length:--text-xs) text-(--text-muted)">
              O preço desta cor é o preço do produto: mude-o na aba Geral.
            </p>
          ) : (
            <>
              <CheckboxField
                label="Preço próprio (desmarcado, segue o preço do produto)"
                checked={ownPrice}
                onCheckedChange={(checked) => {
                  setOwnPrice(checked === true)
                }}
              />
              {ownPrice ? (
                <Field label="Preço da cor" required>
                  {(field) => (
                    <Input
                      {...field}
                      value={price}
                      inputMode="decimal"
                      placeholder="19,90"
                      onChange={(event) => {
                        setPrice(event.target.value)
                      }}
                    />
                  )}
                </Field>
              ) : null}
            </>
          )}
          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={save.isPending}
            onClick={() => {
              save.mutate()
            }}
          >
            Salvar cor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
